import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "@/constants/response-status-codes";
import { TranslationKeys } from "@/contexts/TranslationKeys";
import { Translator } from "@/contexts/translator";
import { genericError } from "@/models/GenericError";
import { ListQuery } from "@/models/ListQuery";
import { Name } from "@/models/Name";
import { OssDevice, OssDeviceWrapper } from "@/models/OssDevice";
import { PaginationModel } from "@/models/Pagination";
import { DeleteEntityResult } from "@/models/services/DeleteEntityResult";
import { StoreEntityResult } from "@/models/services/StoreEntityResult";
import { Deserializer } from 'jsonapi-serializer';
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { getConstraintViolation, getGlobalTranslator, processServerError } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";

const apiConnector = new APIConnector();

const getOssDevice = (ossDeviceAPIObject: any): OssDevice => {
    const translator: Translator = getGlobalTranslator();
    return {
        id: ossDeviceAPIObject['id'],
        name: ossDeviceAPIObject['ossd-name'],
        ossdNo: ossDeviceAPIObject['ossd-no'],
        ossdType: ossDeviceAPIObject['ossd-type'].toString(),
        typeLabel: ossDeviceAPIObject['ossd-type'] === 0 ? translator.byKey(TranslationKeys.doorGroup) : translator.byKey(TranslationKeys.door),
        ossdDeviceId: ossDeviceAPIObject['ossd-device-id'],
        siteId: ossDeviceAPIObject['site']['id'],
        site: ossDeviceAPIObject['site']['site-name'],
    };
}

export const getOssDevices = async (listParameters?: ListQuery): Promise<OssDeviceWrapper> => {
    try {
        const filters: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const siteFields = "fields[site]=siteName";
        const ossDeviceFields = "fields[ossDevice]=site,ossdType,ossdNo,ossdName,ossdDeviceId";
        const url = `ossDevice?${paginationString}&include=site&${siteFields}&${ossDeviceFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const ossDevices: OssDevice[] = data.map((ossDevice): OssDevice => {
                    return getOssDevice(ossDevice);
                });

                return resolve({ rows: ossDevices, pagination: newPagination });
            })
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const storeOssDevice = async (ossDevice: OssDevice): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            data: {
                type: 'ossDevice',
                id: ossDevice.id,
                attributes: {
                    ossdType: ossDevice.ossdType,
                    ossdName: ossDevice.name,
                    ossdDeviceId: ossDevice.ossdDeviceId,
                },
                relationships: {
                    site: {
                        data: {
                            type: 'site',
                            id: ossDevice.siteId
                        }
                    }
                }
            }
        }

        const createOssDevice = !ossDevice.id;
        const url = `ossDevice${createOssDevice ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createOssDevice, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return Promise.reject(processServerError(error));
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createOssDevice) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: content.errors.map(error => getConstraintViolation(error.detail)) })
                    }

                    new Deserializer().deserialize(content, (error, newOssDevice) => {
                        if (error) {
                            return reject({ status: response.status, id: newOssDevice.id, success: false, message: genericError });
                        }
                        return resolve({ id: newOssDevice.id, success: true });
                    })

                    return;
                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: ossDevice.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: ossDevice.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: ossDevice.id, success: false, message: genericError });
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const deleteOssDevice = async (id: string): Promise<DeleteEntityResult> => {
    try {
        const url = `ossDevice/${id}`;
        const ossDevice = {
            data: {
                type: "ossDevice",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, ossDevice)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();
            return reject({status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const getOssDeviceNamesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[]; pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const ossDeviceFields = `fields[ossDevice]=ossdName,site`;
        const siteFields = `fields[site]=siteName`;
        const include = `include=site`;
        const url = `ossDevice?${ossDeviceFields}&${siteFields}&${include}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }

            new Deserializer().deserialize(json, (err: any, apiOssDevices: any[]) => {
                    if (err) {
                        return reject({status: response.status, message: genericError});
                    }

                    const devices: Name[] = apiOssDevices.map((device) => {
                            return {
                                label: device["ossd-name"],
                                value: device["id"],
                            };
                        }
                    );

                    return resolve({rows: devices, pagination: newPagination });
                }
            );
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const getOssDevicesById = async (ossDeviceIds: string | number): Promise<OssDevice> => {
    try {
        const siteFields = "fields[site]=siteName";
        const ossDeviceFields = "fields[ossDevice]=site,ossdType,ossdNo,ossdName,ossdDeviceId";
        const url = `ossDevice/${ossDeviceIds}?include=site&${siteFields}&${ossDeviceFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, ossDevice: any) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                return resolve(getOssDevice(ossDevice));
            })
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const provisionOssCard = async (typeId: number, credentialType: number): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            type: "provisionOssCard",
            attributes: {
                typeId: typeId,
                credentialType: credentialType
            }
        }

        let response;

        await apiConnector.sendData('', true, newRecord, true).then(data => {
            response = data;
        }).catch(error => {
            return Promise.reject(processServerError(error))
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (response.ok === false) {
                    const content = await response.json();
                    return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) ?? genericError })
                }

                return resolve({ id: 0, success: true });
            }

            return reject({ status: response.status, message: genericError });
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}